/**
 * useFontStore — état global partagé des polices disponibles.
 *
 * Centralise la liste des polices accessibles à tous les sélecteurs (FontPicker
 * de l'inspecteur, toolbar inline RichTextToolbar) en regroupant :
 *   1. BASE_FONTS — polices web safe / Google Fonts déjà bundlées (CSS @import)
 *   2. googleFonts — polices Google chargées à la volée par l'utilisateur,
 *      persistées en localStorage et re-injectées au prochain refresh
 *   3. localFonts — polices système chargées via `window.queryLocalFonts()`
 *      (API expérimentale Chrome/Edge, permission gated, session uniquement)
 *   4. uploadedFonts — polices IMPORTÉES par l'utilisateur (.ttf/.otf), fichier
 *      stocké en IndexedDB, ré-enregistré au démarrage → fiable (indépendant de
 *      l'OS et de toute permission), et embarqué tel quel dans le PDF à l'export.
 */
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { putFont, getAllFonts, deleteFont } from '../utils/fontDB';

/**
 * Familles proposées d'office. **Cette liste n'est pas qu'un menu** : `exportFonts.js`
 * s'en sert pour décider qu'une famille est « déjà gérée » et n'a donc PAS besoin
 * d'être acheminée depuis le poste au service de rendu. Y inscrire une famille,
 * c'est promettre qu'elle sera disponible à l'impression.
 *
 * **Avant d'en ajouter une, rejouer `banc/banc-polices-rendu.sh`** : il mesure la
 * largeur peinte des deux côtés et dit si la promesse tient. Ne pas se fier à
 * `fc-match` dans l'image de rendu — il décrit fontconfig, pas le moteur qui
 * compose, et il a fait annoncer un défaut Georgia inexistant le 2026-08-28.
 */
export const BASE_FONTS = [
    "Cinzel Decorative", "Orbitron", "Rajdhani", "Roboto",
    "Oswald", "Montserrat", "Open Sans", "Lato",
    "Poppins", "Bebas Neue", "Playfair Display", "Anton",
    "Arial", "Arial Narrow", "Helvetica", "Georgia", "Times New Roman",
    "Courier New", "DM Sans", "JetBrains Mono",
    // L'UFI des étiquettes de l'agent (24/10/2026) : une police de terminal au zéro pointé, assez étroite pour que
    // le code tienne sur une ligne de 20 mm en 5,5 pt (18,4 mm ; JetBrains Mono en fait 22).
    "Ubuntu Mono",
];


/**
 * Encode le nom d'une font pour l'URL Google Fonts.
 * "Roboto Slab" → "Roboto+Slab"
 */
function encodeFontFamily(name) {
    return name.trim().replace(/\s+/g, '+');
}


/**
 * Génère l'URL CSS Google Fonts pour une font donnée, en chargeant un éventail
 * de graisses utiles (300, 400, 500, 600, 700) en regular et italic quand
 * disponibles. `display=swap` évite le FOIT (flash of invisible text).
 */
function googleFontUrl(name) {
    const family = encodeFontFamily(name);
    return `https://fonts.googleapis.com/css2?family=${family}:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400;1,700&display=swap`;
}


/**
 * Injecte un <link> dans le <head> qui charge une font Google. Idempotent :
 * si la font est déjà injectée (link[data-google-font="<name>"] présent), no-op.
 *
 * Retourne une Promise qui se résout quand la font est prête (chargée et
 * disponible pour le rendu). Rejette si le navigateur n'arrive pas à charger
 * (font name inconnu côté Google → CSS vide, la promise se résout quand
 * même mais sans qu'aucune face ne soit ajoutée).
 */
function injectGoogleFont(name) {
    return new Promise((resolve, reject) => {
        if (!name || typeof name !== 'string') {
            reject(new Error('Nom de font invalide'));
            return;
        }
        const trimmed = name.trim();
        const selector = `link[data-google-font="${CSS.escape(trimmed)}"]`;
        if (document.querySelector(selector)) {
            // Déjà chargée → résout immédiatement
            resolve(trimmed);
            return;
        }

        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = googleFontUrl(trimmed);
        link.dataset.googleFont = trimmed;
        link.crossOrigin = 'anonymous';

        // onload : le CSS est téléchargé. On laisse 100ms pour que le browser
        // parse les @font-face avant de résoudre.
        link.onload = () => {
            // Force le browser à essayer de charger la font dans son cache
            // FontFaceSet en demandant explicitement document.fonts.load.
            // Ça permet à document.fonts.check() de retourner true ensuite.
            try {
                document.fonts.load(`12px "${trimmed}"`).then(() => resolve(trimmed));
            } catch (_) {
                // Pas critique si load échoue — la font est dans le CSS,
                // elle sera utilisée dès que rendue dans un calque.
                setTimeout(() => resolve(trimmed), 100);
            }
        };
        link.onerror = () => {
            // Retire le link cassé pour permettre une nouvelle tentative
            document.head.removeChild(link);
            reject(new Error(`Impossible de charger la font "${trimmed}" depuis Google Fonts.`));
        };

        document.head.appendChild(link);
    });
}


// ─── Polices IMPORTÉES (upload de fichier) ───
// Réfs FontFace ajoutées à document.fonts, conservées pour pouvoir les retirer.
const _uploadedFaces = new Map();

/** Déduit un nom de famille propre à partir du nom de fichier. */
function familyFromFilename(name) {
    return String(name || 'Police importée')
        .replace(/\.(ttf|otf|woff2?|ttc)$/i, '')
        .replace(/_+/g, ' ')
        .trim() || 'Police importée';
}

/** Enregistre une police (ArrayBuffer) dans document.fonts pour l'AFFICHAGE éditeur. */
async function registerFontFace(family, arrayBuffer) {
    try {
        const face = new FontFace(family, arrayBuffer);
        await face.load();
        document.fonts.add(face);
        _uploadedFaces.set(family, face);
        return true;
    } catch (_) {
        return false;
    }
}


export const useFontStore = create(
    persist(
        (set, get) => ({
            googleFonts: [],         // ['Inter', 'Roboto Slab', ...]
            localFonts: [],          // pas persisté (rechargé chaque session)
            uploadedFonts: [],       // polices importées (.ttf/.otf) — fichiers en IndexedDB
            isLoading: false,        // pour OS fonts
            isLoadingGoogle: false,  // pour ajout Google
            isUploading: false,      // pour import d'un fichier de police
            error: null,
            // Refus propre à « Polices OS ». Séparé d'`error`, où écrivent aussi
            // l'ajout Google et l'import de fichier : le sélecteur affiche déjà ces
            // deux-là, et faire lire `error` au panneau doublait leur message.
            localFontsError: null,

            /** Liste fusionnée BASE_FONTS + importées + googleFonts + localFonts (sans doublons) */
            allFonts: () => {
                const { googleFonts, localFonts, uploadedFonts } = get();
                const seen = new Set(BASE_FONTS);
                const out = [...BASE_FONTS];
                for (const f of uploadedFonts) if (!seen.has(f)) { seen.add(f); out.push(f); }
                for (const f of googleFonts)   if (!seen.has(f)) { seen.add(f); out.push(f); }
                for (const f of localFonts)    if (!seen.has(f)) { seen.add(f); out.push(f); }
                return out;
            },

            /**
             * Charge une font Google par nom et l'ajoute à la liste persistée.
             * Si la font est déjà connue, no-op (mais re-injecte le <link>
             * au cas où il aurait été retiré).
             */
            loadGoogleFont: async (rawName) => {
                const name = (rawName || '').trim();
                if (!name) throw new Error('Nom vide');
                set({ isLoadingGoogle: true, error: null });
                try {
                    await injectGoogleFont(name);
                    const { googleFonts } = get();
                    if (!googleFonts.includes(name)) {
                        set({ googleFonts: [...googleFonts, name].sort() });
                    }
                    set({ isLoadingGoogle: false });
                    return name;
                } catch (err) {
                    set({ isLoadingGoogle: false, error: err.message });
                    throw err;
                }
            },

            /**
             * Supprime une font Google de la liste personnelle. Le <link>
             * injecté est laissé en place (anodin, ~quelques Ko de CSS) ;
             * il sera nettoyé au prochain refresh.
             */
            removeGoogleFont: (name) => {
                const { googleFonts } = get();
                set({ googleFonts: googleFonts.filter(f => f !== name) });
            },

            /**
             * Ré-injecte tous les <link> Google Fonts persistés au démarrage
             * de l'app (appelé une seule fois depuis App.jsx). Sans ça, après
             * un refresh, les noms de fonts sont là mais les CSS pas chargés
             * → les calques qui les utilisent retombent sur la font par défaut.
             */
            rehydrateGoogleFonts: () => {
                const { googleFonts } = get();
                for (const name of googleFonts) {
                    // Best-effort : on ignore les erreurs (font supprimée
                    // côté Google entre temps, etc.). La font apparaît
                    // toujours dans la liste mais ne s'affichera pas.
                    injectGoogleFont(name).catch(() => {});
                }
            },

            /**
             * Chargement des polices système (Chrome/Edge, API expérimentale).
             *
             * AVANT : ces deux échecs passaient par `alert()`, alors que les quatre
             * autres erreurs de police (format, taille, police illisible, Google
             * Fonts introuvable) sont rendues dans le panneau. Deux voies pour la
             * même chose, dont une qui bloque la page et sort de l'endroit où
             * l'utilisateur regarde. Le message est désormais posé dans
             * `localFontsError`, que le sélecteur de police affiche sous le bouton,
             * à côté des autres refus.
             *
             * Renvoie `true` si les polices ont été chargées, `false` sinon ;
             * en cas d'échec, `localFontsError` porte le message à afficher.
             */
            loadLocalFonts: async () => {
                if (!('queryLocalFonts' in window)) {
                    set({
                        localFontsError: "Votre navigateur ne permet pas de lire les polices du système. Utilisez Google Chrome ou Edge, ou importez le fichier de la police avec « Importer une police ».",
                    });
                    return false;
                }
                set({ isLoading: true, localFontsError: null });
                try {
                    const fonts = await window.queryLocalFonts();
                    const fontNames = Array.from(new Set(fonts.map(f => f.family))).sort();
                    set({ localFonts: fontNames, isLoading: false });
                    return true;
                } catch (err) {
                    set({
                        isLoading: false,
                        localFontsError: "L'accès aux polices du système a été refusé. Relancez « Polices OS » et autorisez l'accès quand le navigateur vous le demande, ou importez le fichier de la police avec « Importer une police ».",
                    });
                    return false;
                }
            },

            /**
             * Importe un fichier de police (.ttf/.otf/.woff/.woff2) : enregistre la
             * police pour l'affichage (FontFace) ET conserve le fichier en IndexedDB
             * pour qu'elle survive au refresh et soit embarquée dans le PDF à l'export.
             * Renvoie le nom de famille créé.
             */
            uploadFont: async (file) => {
                if (!file) throw new Error('Aucun fichier');
                if (!/\.(ttf|otf|woff2?)$/i.test(file.name)) {
                    throw new Error('Format non supporté (.ttf, .otf, .woff, .woff2)');
                }
                // 20 Mo : les polices décoratives/variables dépassent souvent 8 Mo.
                // Plafond gardé sous ~24 Mo car la police est ré-acheminée en base64
                // (~+33 %) à CHAQUE export vers le service de rendu (limite de requête
                // Cloud Run = 32 Mo). 20 Mo → ~27 Mo encodé, marge de sécurité.
                if (file.size > 20 * 1024 * 1024) {
                    throw new Error('Fichier trop volumineux (max 20 Mo)');
                }
                set({ isUploading: true, error: null });
                try {
                    const ab = await file.arrayBuffer();
                    let family = familyFromFilename(file.name);
                    // Évite d'écraser une famille existante (bundlée / Google / déjà importée).
                    const taken = new Set(
                        [...BASE_FONTS, ...get().googleFonts, ...get().uploadedFonts]
                            .map((s) => s.toLowerCase())
                    );
                    if (taken.has(family.toLowerCase())) family = `${family} (importée)`;
                    const ok = await registerFontFace(family, ab.slice(0));
                    if (!ok) throw new Error('Police illisible par le navigateur');
                    const blob = new Blob([ab], { type: file.type || 'font/ttf' });
                    await putFont({ family, blob, type: file.type || '', addedAt: Date.now() });
                    const { uploadedFonts } = get();
                    if (!uploadedFonts.includes(family)) {
                        set({ uploadedFonts: [...uploadedFonts, family] });
                    }
                    set({ isUploading: false });
                    return family;
                } catch (err) {
                    set({ isUploading: false, error: err.message });
                    throw err;
                }
            },

            /** Supprime une police importée (IndexedDB + FontFace + liste). */
            removeUploadedFont: async (family) => {
                try { await deleteFont(family); } catch (_) { /* ignore */ }
                const face = _uploadedFaces.get(family);
                if (face) {
                    try { document.fonts.delete(face); } catch (_) { /* ignore */ }
                    _uploadedFaces.delete(family);
                }
                set({ uploadedFonts: get().uploadedFonts.filter((f) => f !== family) });
            },

            /**
             * Recharge les polices importées depuis IndexedDB au démarrage et les
             * ré-enregistre dans document.fonts (appelé une fois depuis App.jsx).
             * Sans ça, après un refresh, les calques qui les utilisent retombent
             * sur la police par défaut.
             */
            rehydrateUploadedFonts: async () => {
                try {
                    const entries = await getAllFonts();
                    const names = [];
                    for (const e of entries) {
                        try {
                            const ab = await e.blob.arrayBuffer();
                            if (await registerFontFace(e.family, ab)) names.push(e.family);
                        } catch (_) { /* skip cette police */ }
                    }
                    if (names.length) set({ uploadedFonts: names });
                } catch (_) { /* IndexedDB indisponible — best-effort */ }
            },
        }),
        {
            name: 'labelforge-font-store',
            storage: createJSONStorage(() => localStorage),
            // On ne persiste QUE googleFonts (les autres sont session-only)
            partialize: (state) => ({ googleFonts: state.googleFonts }),
        }
    )
);
